import { apiAxios } from '@/api/apiConfig';
import type { AxiosRequestConfig } from 'axios';
import type { PlanCode, PaymentStatus, PaymentMethod, ReceiptType, ReceiptIssueStatus } from '@/api/planApi';

// Workspace 통합 모델 3단계 API (백엔드 로드맵 §9) - 레거시 teamApi/planApi와 병행.
// 백엔드 workspace.api.enabled가 꺼져 있으면 이 경로들은 전부 404를 반환한다(컨트롤러
// 빈 자체가 생성 안 됨) - 로컬 개발 시 WORKSPACE_API_ENABLED=true 환경변수로 켜서 사용.

export type WorkspacePlan = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
export type WorkspaceStatus = 'ACTIVE' | 'INACTIVE' | 'PAST_DUE';
export type WorkspaceMemberRole = 'OWNER' | 'MEMBER';

/** GET /api/workspaces/my/teams 응답 - 레거시 TeamSummary와 별개 타입(축소 DTO) */
export interface WorkspaceSummary {
  workspaceId: number;
  name: string;
  personal: boolean;
  myRole: WorkspaceMemberRole;
  plan: WorkspacePlan;
  monthlyLimit: number;
  status: WorkspaceStatus;
  memberCount: number;
  /** 팀 워크스페이스면 레거시 teams.id, 개인 워크스페이스면 null - 레거시 쓰기 액션(teamApi.*)과 이어 붙일 때 사용 */
  legacyTeamId: number | null;
}

/** GET /api/workspaces/teams/{workspaceId}/usage 응답 - 레거시 TeamUsage와 별개 타입(memberStats/엔터프라이즈 계약 제외) */
export interface WorkspaceTeamUsage {
  workspaceId: number;
  teamName: string;
  plan: WorkspacePlan;
  monthlyLimit: number;
  totalUsed: number;
  remaining: number;
  billingStartDate: string | null;
  billingEndDate: string | null;
}

/**
 * GET /api/workspaces/my/billing 응답 - 레거시 PaymentRecord와 별개 타입(축소 DTO).
 * teamId/contractId/monthlyLimit/monthlyPrice/contractName/teamName/teamRole은
 * 이 DTO에 의도적으로 없음(BE가 아직 "후속 조각"으로 미구현) - FE에서도 아예
 * 타입에 선언하지 않아 실수로 읽을 수 없게 한다. cashReceiptIssued도 legacy와
 * null 처리 방식이 달라(legacy는 항상 boolean으로 정규화, 이쪽은 raw nullable)
 * 의도적으로 타입에서 제외했다 - src/lib/workspacePaymentOverlay.ts 참고.
 */
export interface WorkspacePaymentRecordResponse {
  id: number;
  plan: PlanCode;
  amount: number;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  billingMonth: string;
  createdAt: string;
  confirmedAt: string | null;
  canCancel: boolean;
  refundAmount: number | null;
  refundedAt: string | null;
  refundCompletedAt: string | null;
  receiptType: ReceiptType | null;
  receiptIssueStatus: ReceiptIssueStatus | null;
  receiptIssuedAt: string | null;
  receiptIssueFailReason: string | null;
  isDowngrade: boolean | null;
}

export interface WorkspacePaymentPage {
  content: WorkspacePaymentRecordResponse[];
  totalElements: number;
  totalPages: number;
  number: number;
}

/**
 * GET /api/workspaces/my/plan 응답 - 레거시 PlanInfo와 별개 타입(축소 DTO).
 * plan/pendingPlan/status/enterpriseContract는 BE가 응답에 포함하지만 이 DTO에
 * 의도적으로 선언하지 않는다 - plan은 업그레이드/다운그레이드 분기 결정에,
 * enterpriseContract는 PlanPage.tsx의 변경 차단 게이트(활성 계약 여부)에 각각
 * 쓰이는 위험 필드라 overlay 대상에서 제외했다(src/lib/workspacePlanOverlay.ts 참고).
 */
export interface WorkspacePlanOverlayResponse {
  monthlyLimit: number;
  billingCycleDay: number | null;
  canDowngrade: boolean;
  downgradeReason: string | null;
  changePlanReason: string | null;
}

export const workspaceApi = {
  getMyTeams: (config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspaceSummary[]>('/workspaces/my/teams', config),

  getTeamUsage: (workspaceId: number) =>
    apiAxios.get<WorkspaceTeamUsage>(`/workspaces/teams/${workspaceId}/usage`),

  getMyBilling: (params: { page: number; size: number }, config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspacePaymentPage>('/workspaces/my/billing', { ...config, params }),

  getMyPlan: (config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspacePlanOverlayResponse>('/workspaces/my/plan', config),
};
